import React, { useState } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { signInWithEmailAndPassword } from "firebase/auth";
import { auth, db } from '../firebase';
import { ref, get } from "firebase/database";
import './Auth.css';
import { useAuth } from '../AuthContext';
import { nitrologo } from "../utilities/images";

// A simple navbar for the auth pages, inspired by Uber's minimalist header
const AuthHeader = () => (
  <header className="uber-auth-header">
    <Link to="/">
      <img src={nitrologo} alt="9troRide Logo" />
    </Link>
  </header>
);

function RideLogin() {
  const [step, setStep] = useState('identifier'); // 'identifier' or 'password'
  const [identifier, setIdentifier] = useState(''); // Can be email or phone
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  
  // Resolve redirect destination, restoring exact path & query parameters
  const getRedirectPath = () => {
    const defaultPath = '/';
    if (!location.state?.from) return defaultPath;
    
    let fromPath = '/';
    if (typeof location.state.from === 'string') {
      fromPath = location.state.from;
    } else if (location.state.from?.pathname) {
      fromPath = `${location.state.from.pathname}${location.state.from.search || ''}`;
    }
      
    const isAuthPath = fromPath.startsWith('/ride-login') || 
                       fromPath.startsWith('/ride-signup') || 
                       fromPath.startsWith('/login') || 
                       fromPath.startsWith('/signup');
                       
    if (isAuthPath) return defaultPath;
    return fromPath;
  };
  const from = getRedirectPath();
  const { signOut } = useAuth();

  const handleContinue = (e) => {
    e.preventDefault();
    if (!identifier) {
      setError('Please enter your email or mobile number.');
      return;
    }
    // Basic email validation, can be improved
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(identifier)) {
        setError('Please enter a valid email address.');
        return;
    }
    setError('');
    setStep('password');
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    setError('');
    if (!password) {
      setError('Please enter your password.');
      return;
    }
    setLoading(true);
    try {
      const userCredential = await signInWithEmailAndPassword(auth, identifier, password);
      const user = userCredential.user;

      const riderRef = ref(db, 'riders/' + user.uid);
      const snapshot = await get(riderRef);

      if (snapshot.exists()) {
        console.log('Logged in user:', user);
        navigate(from, { replace: true });
      } else {
        const driverRef = ref(db, 'drivers/' + user.uid);
        const driverSnapshot = await get(driverRef);
        await signOut();
        if (driverSnapshot.exists()) {
          setError("This appears to be a driver account. Please use the driver login.");
        } else {
          setError("No user account found for this email. Please sign up.");
        }
        console.error("Login failed: User is not a registered rider.");
      }
    } catch (error) {
      setError(error.message.replace('Firebase: ', ''));
      console.error("Error logging in:", error);
    } finally {
      setLoading(false);
    }
  };

  const renderIdentifierStep = () => (
    <>
      <div className="auth-header">
        <h1>What's your email or mobile number?</h1>
      </div>
      <form onSubmit={handleContinue} className="auth-form">
        <div className="auth-input-group">
          <div className="auth-input-wrapper">
            <input
              type="text"
              id="identifier"
              placeholder="Enter email or mobile number"
              value={identifier}
              onChange={(e) => setIdentifier(e.target.value)}
              required
              autoFocus
            />
            <div className="auth-input-icons">
              {identifier && (
                <button type="button" className="auth-input-icon-btn" onClick={() => setIdentifier('')}>
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><line x1="18" y1="6" x2="6" y2="18"></line><line x1="6" y1="6" x2="18" y2="18"></line></svg>
                </button>
              )}
            </div>
          </div>
        </div>
        {error && <p className="auth-error" style={{textAlign: 'center'}}>{error}</p>}
        <button type="submit" className="auth-submit-btn">
          Continue
        </button>
      </form>
      <div className="auth-redirect">
        <p>Don't have an account? <Link to="/ride-signup" state={{ from: location.state?.from }}>Sign up</Link></p>
      </div>
    </>
  );

  const renderPasswordStep = () => (
    <>
      <div className="auth-header">
        <h1>Enter your password</h1>
      </div>
      <form onSubmit={handleLogin} className="auth-form">
        <div className="auth-input-group">
          <input
            type="email"
            value={identifier}
            readOnly
            disabled
            className="disabled-input"
          />
        </div>
        <div className="auth-input-group">
          <div className="auth-input-wrapper">
            <input
              type={showPassword ? 'text' : 'password'}
              id="password"
              placeholder="Password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              autoFocus
            />
            <div className="auth-input-icons">
              {password && (
                <button type="button" className="auth-input-icon-btn" onClick={() => setPassword('')}>
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><line x1="18" y1="6" x2="6" y2="18"></line><line x1="6" y1="6" x2="18" y2="18"></line></svg>
                </button>
              )}
              <button type="button" className="auth-input-icon-btn" onClick={() => setShowPassword(!showPassword)}>
                {showPassword ? (
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"></path><circle cx="12" cy="12" r="3"></circle></svg>
                ) : (
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24"></path><line x1="1" y1="1" x2="23" y2="23"></line></svg>
                )}
              </button>
            </div>
          </div>
        </div>
        {error && <p className="auth-error" style={{textAlign: 'center'}}>{error}</p>}
        <div className="auth-form-footer">
            <Link to="#" onClick={(e) => { e.preventDefault(); setStep('identifier'); setError(''); }}>Use a different email</Link>
            <Link to="/forgot-password">Forgot password?</Link>
        </div>
        <button type="submit" className="auth-submit-btn" disabled={loading}>
          {loading ? 'Logging In...' : 'Log In'}
        </button>
      </form>
    </>
  );

  return (
    <div className="uber-auth-page-wrapper">
      <AuthHeader />
      <div className="uber-auth-container">
        {step === 'identifier' ? renderIdentifierStep() : renderPasswordStep()}
      </div>
    </div>
  );
}

export default RideLogin;